import React, { useCallback, useEffect, useRef, useState } from "react";
import { figureRatioClass, blockTechnicalLabelClass } from "./layoutTokens";

export function ControlBar({
  onPrev,
  onNext,
  onPlayPause,
  playPaused = true,
  showPlay = true,
  caption,
  counterText,
  disablePrev,
  disableNext,
}) {
  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-2 px-2.5 py-2 font-mono text-meta uppercase text-content-secondary dark:text-neutral-400 ${
        caption ? "border-t border-border dark:border-neutral-800" : ""
      }`}
    >
      <div className="flex gap-1 shrink-0">
        <button
          type="button"
          onClick={onPrev}
          disabled={disablePrev}
          className="w-[22px] h-[22px] flex items-center justify-center border border-content-subtle dark:border-neutral-600 bg-white dark:bg-black text-black dark:text-white hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black disabled:opacity-35 transition-colors"
          aria-label="Previous"
        >
          ‹
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={disableNext}
          className="w-[22px] h-[22px] flex items-center justify-center border border-content-subtle dark:border-neutral-600 bg-white dark:bg-black text-black dark:text-white hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black disabled:opacity-35 transition-colors"
          aria-label="Next"
        >
          ›
        </button>
        {showPlay && onPlayPause && (
          <button
            type="button"
            onClick={onPlayPause}
            className="h-[22px] px-2 text-meta tracking-[0.08em] border border-content-subtle dark:border-neutral-600 bg-white dark:bg-black hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors"
          >
            {playPaused ? "Play" : "Pause"}
          </button>
        )}
      </div>
      {caption ? (
        <div className="flex-1 min-w-[120px] text-center font-sans normal-case text-body leading-snug text-content-secondary dark:text-neutral-400 px-2">
          {caption}
        </div>
      ) : (
        <div className="flex-1 min-w-0 min-h-px" aria-hidden />
      )}
      <div className="font-mono text-meta tabular-nums whitespace-nowrap shrink-0">{counterText}</div>
    </div>
  );
}

export function SequenceDemo({ captions }) {
  const n = captions.length;
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(true);
  const timerRef = useRef(null);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => () => stopTimer(), [stopTimer]);

  const go = (k) => {
    setI(((k % n) + n) % n);
  };

  const togglePlay = () => {
    if (!paused) {
      stopTimer();
      setPaused(true);
      return;
    }
    setPaused(false);
    timerRef.current = setInterval(() => {
      setI((prev) => (prev + 1) % n);
    }, 2400);
  };

  useEffect(() => {
    if (paused) stopTimer();
    return undefined;
  }, [paused, stopTimer]);

  const pad = (x) => (x < 10 ? `0${x}` : `${x}`);

  return (
    <div className="border border-border dark:border-neutral-800 bg-white dark:bg-black max-w-[380px] w-full mx-auto">
      <div className="relative aspect-[4/3] overflow-hidden bg-neutral-200 dark:bg-neutral-800">
        <div
          className="flex h-full transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${i * 100}%)` }}
        >
          {captions.map((_, idx) => (
            <div
              key={idx}
              className="min-w-full h-full flex items-center justify-center font-mono text-body uppercase tracking-[0.12em] text-content-secondary relative"
              style={{
                backgroundImage:
                  idx % 2 === 0
                    ? "repeating-linear-gradient(45deg, rgba(10,10,10,.06) 0 1px, transparent 1px 8px)"
                    : "repeating-linear-gradient(135deg, rgba(10,10,10,.07) 0 1px, transparent 1px 8px)",
              }}
            >
              <span className="absolute left-2 top-2 bg-black dark:bg-white text-white dark:text-black font-mono text-meta px-1.5 py-0.5">
                {pad(idx + 1)} / {pad(n)}
              </span>
              Slide {pad(idx + 1)}
            </div>
          ))}
        </div>
      </div>
      <ControlBar
        onPrev={() => {
          stopTimer();
          setPaused(true);
          go(i - 1);
        }}
        onNext={() => {
          stopTimer();
          setPaused(true);
          go(i + 1);
        }}
        onPlayPause={togglePlay}
        playPaused={paused}
        showPlay
        caption={captions[i]}
        counterText={
          <>
            <span className="text-black dark:text-white font-medium">{pad(i + 1)}</span> / {pad(n)}
          </>
        }
        disablePrev={false}
        disableNext={false}
      />
    </div>
  );
}

export function DiagramStepsDemo({ captions }) {
  const n = captions.length;
  const [i, setI] = useState(0);

  const pad = (x) => (x < 10 ? `0${x}` : `${x}`);

  return (
    <div className="border border-border dark:border-neutral-800 bg-white dark:bg-black max-w-[380px] w-full mx-auto">
      <div className="relative aspect-[4/3] overflow-hidden bg-white dark:bg-black">
        <svg viewBox="0 0 320 200" className="w-full h-full text-black dark:text-white" aria-hidden>
          <g style={{ opacity: i >= 0 ? 1 : 0.2 }}>
            <rect x="20" y="80" width="60" height="40" fill="none" stroke="currentColor" />
            <text x="50" y="105" textAnchor="middle" className="fill-current font-mono" style={{ fontSize: 9 }}>
              SITE
            </text>
          </g>
          <g style={{ opacity: i >= 1 ? 1 : 0.2 }}>
            <line x1="80" y1="100" x2="120" y2="100" stroke="currentColor" />
            <rect x="120" y="80" width="60" height="40" fill="none" stroke="currentColor" />
            <text x="150" y="105" textAnchor="middle" className="fill-current font-mono" style={{ fontSize: 9 }}>
              RULES
            </text>
          </g>
          <g style={{ opacity: i >= 2 ? 1 : 0.2 }}>
            <line x1="180" y1="100" x2="220" y2="100" stroke="currentColor" />
            <rect x="220" y="60" width="60" height="80" fill="none" stroke="currentColor" />
            <text x="250" y="100" textAnchor="middle" className="fill-current font-mono" style={{ fontSize: 9 }}>
              FORM
            </text>
          </g>
          <g style={{ opacity: i >= 3 ? 1 : 0.2 }}>
            <line x1="150" y1="120" x2="150" y2="160" stroke="currentColor" strokeDasharray="3 3" />
            <rect x="100" y="160" width="100" height="30" fill="none" stroke="currentColor" />
            <text x="150" y="178" textAnchor="middle" className="fill-current font-mono" style={{ fontSize: 8 }}>
              REVIEW
            </text>
          </g>
        </svg>
        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
          {captions.map((_, idx) => (
            <button
              key={idx}
              type="button"
              aria-label={`Step ${idx + 1}`}
              onClick={() => setI(idx)}
              className={`h-[3px] w-3.5 border-0 ${idx <= i ? "bg-black dark:bg-white" : "bg-neutral-300 dark:bg-neutral-600"}`}
            />
          ))}
        </div>
      </div>
      <ControlBar
        showPlay={false}
        onPrev={() => setI((x) => Math.max(0, x - 1))}
        onNext={() => setI((x) => Math.min(n - 1, x + 1))}
        caption={captions[i]}
        counterText={
          <>
            <span className="text-black dark:text-white font-medium">{pad(i + 1)}</span> / {pad(n)}
          </>
        }
        disablePrev={i === 0}
        disableNext={i >= n - 1}
      />
    </div>
  );
}

export function LiveCubeDemo() {
  const [rx, setRx] = useState(-18);
  const [ry, setRy] = useState(-24);
  const [autoOn, setAutoOn] = useState(false);
  const dragging = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const autoRef = useRef(null);

  const stopAuto = useCallback(() => {
    if (autoRef.current) {
      clearInterval(autoRef.current);
      autoRef.current = null;
    }
    setAutoOn(false);
  }, []);

  const startAuto = useCallback(() => {
    if (autoRef.current) return;
    autoRef.current = setInterval(() => {
      setRy((y) => y + 0.9);
    }, 32);
    setAutoOn(true);
  }, []);

  const toggleAuto = () => {
    if (autoRef.current) stopAuto();
    else startAuto();
  };

  useEffect(() => () => stopAuto(), [stopAuto]);

  return (
    <div className="border border-border dark:border-neutral-800 bg-white dark:bg-black max-w-[380px] w-full mx-auto">
      <div
        className="relative aspect-[4/3] flex items-center justify-center bg-neutral-50 dark:bg-neutral-950"
        style={{ perspective: "600px" }}
        onPointerDown={(e) => {
          dragging.current = true;
          stopAuto();
          last.current = { x: e.clientX, y: e.clientY };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (!dragging.current) return;
          const dx = e.clientX - last.current.x;
          const dy = e.clientY - last.current.y;
          last.current = { x: e.clientX, y: e.clientY };
          setRx((prev) => Math.max(-85, Math.min(85, prev - dy * 0.35)));
          setRy((prev) => prev + dx * 0.45);
        }}
        onPointerUp={() => {
          dragging.current = false;
        }}
        onDoubleClick={() => {
          stopAuto();
          setRx(-18);
          setRy(-24);
        }}
      >
        <p className="absolute top-2 left-0 right-0 text-center font-mono text-meta uppercase tracking-[0.08em] text-content-muted pointer-events-none">
          Drag to rotate · double-click reset
        </p>
        <div
          className="relative w-[55%] aspect-square [transform-style:preserve-3d]"
          style={{
            transform: `rotateX(${rx}deg) rotateY(${ry}deg)`,
            transformStyle: "preserve-3d",
          }}
        >
          {["Front", "Back", "Right", "Left", "Top", "Bot"].map((face, idx) => {
            const transforms = [
              "translateZ(50%)",
              "translateZ(-50%) rotateY(180deg)",
              "rotateY(90deg) translateZ(50%)",
              "rotateY(-90deg) translateZ(50%)",
              "rotateX(90deg) translateZ(50%)",
              "rotateX(-90deg) translateZ(50%)",
            ];
            return (
              <div
                key={face}
                className="absolute inset-0 border border-black dark:border-white bg-white/70 dark:bg-black/40 flex items-center justify-center font-mono text-meta uppercase text-content-secondary"
                style={{ transform: transforms[idx], backfaceVisibility: "hidden" }}
              >
                {face}
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 px-2.5 py-2 border-t border-border dark:border-neutral-800 font-mono text-meta uppercase text-content-secondary dark:text-neutral-400">
        <div className="flex gap-1">
          <button
            type="button"
            onClick={toggleAuto}
            className="h-[22px] px-2 text-meta border border-content-subtle dark:border-neutral-600 bg-white dark:bg-black hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors"
          >
            {autoOn ? "Pause" : "Rotate"}
          </button>
          <button
            type="button"
            onClick={() => {
              stopAuto();
              setRx(-18);
              setRy(-24);
            }}
            className="h-[22px] px-2 text-meta border border-content-subtle dark:border-neutral-600 bg-white dark:bg-black hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors"
          >
            Reset
          </button>
        </div>
        <div className="flex-1 text-center font-sans normal-case text-body text-content-secondary px-2">
          Final massing — specimen block
        </div>
        <div className="font-mono text-meta tabular-nums">3D</div>
      </div>
    </div>
  );
}

/**
 * Image sequence player — same control bar as SequenceDemo but takes real image URLs.
 * Pin the viewport to one of the four canonical aspect ratios via `ratio`.
 *
 * Props:
 *   images   — array of image URLs
 *   captions — optional array; falls back to "Frame NN"
 *   label    — small mono-cap label shown above the player
 *   ratio    — "banner" | "video" | "classic" (default) | "square"
 *   intervalMs — autoplay interval (default 2600)
 */
export function ImageSequence({ images, captions = [], label, ratio = "classic", intervalMs = 2600 }) {
  const n = images.length;
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(true);
  const timerRef = useRef(null);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => () => stopTimer(), [stopTimer]);

  const pad = (x) => (x < 10 ? `0${x}` : `${x}`);
  const cap = captions[i] || `Frame ${pad(i + 1)}`;

  const togglePlay = () => {
    if (!paused) {
      stopTimer();
      setPaused(true);
      return;
    }
    setPaused(false);
    timerRef.current = setInterval(() => setI((p) => (p + 1) % n), intervalMs);
  };

  useEffect(() => {
    if (paused) stopTimer();
  }, [paused, stopTimer]);

  return (
    <div className="border border-border dark:border-neutral-800 bg-white dark:bg-black w-full flex flex-col">
      {label ? (
        <div className={`${blockTechnicalLabelClass} px-2.5 py-1.5 border-b border-border dark:border-neutral-800 bg-surface-muted dark:bg-neutral-950`}>
          <span aria-hidden className="mr-1.5 text-content-subtle dark:text-neutral-600">▸</span>
          {label}
        </div>
      ) : null}
      <div className={`relative w-full ${figureRatioClass(ratio)} bg-neutral-100 dark:bg-neutral-900 overflow-hidden`}>
        <img
          src={images[i]}
          alt={cap}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-contain"
        />
      </div>
      <ControlBar
        onPrev={() => {
          stopTimer();
          setPaused(true);
          setI((x) => (x - 1 + n) % n);
        }}
        onNext={() => {
          stopTimer();
          setPaused(true);
          setI((x) => (x + 1) % n);
        }}
        onPlayPause={togglePlay}
        playPaused={paused}
        counterText={
          <>
            <span className="text-black dark:text-white font-medium">{pad(i + 1)}</span> / {pad(n)}
          </>
        }
      />
    </div>
  );
}
